import { PopUpButton, type PopUpOption } from '../../../ui';
import { useMyTeamMemberId, useOwnerChoice } from './hooks';
import {
  type OwnedRow,
  ownerButtonText,
  ownerFilterOf,
  ownerOptions,
  resolveOwnerChoice,
} from './ownerMenu';

/**
 * THE OWNER MENU OF THE THREE PAGES (brief FE-FOUNDATION section 10): the pop-up button
 * "Owner: All" over the shared rules of `ownerMenu.ts` and the remembered choice. Its
 * accessible name is the visible "Owner: All", so what a person hears is what they see.
 *
 * The page calls `useOwnerMenu(rows, extra)` once - its rows give the owners and the counts
 * ("the number of rows that option shows on the calling page") - filters by `filter`, and draws
 * `<OwnerMenu menu={...} context="toolbar" />` where it wants the control.
 */
export interface OwnerMenuState {
  readonly options: readonly PopUpOption[];
  /** The resolved choice: a stored value that is no longer an option reads as All. */
  readonly choice: string;
  readonly choose: (next: string) => void;
  /** What the page filters by: `all`, a team member id, or the page's own value. */
  readonly filter: string;
  readonly myTeamMemberId: string | null;
}

export function useOwnerMenu(
  rows: readonly OwnedRow[],
  extra: readonly PopUpOption[] = [],
): OwnerMenuState {
  const myTeamMemberId = useMyTeamMemberId();
  const [stored, choose] = useOwnerChoice();
  const options = ownerOptions(rows, myTeamMemberId, extra);
  const choice = resolveOwnerChoice(stored, options);
  return { options, choice, choose, filter: ownerFilterOf(choice, myTeamMemberId), myTeamMemberId };
}

export function OwnerMenu({
  menu,
  context,
}: {
  readonly menu: OwnerMenuState;
  readonly context: 'toolbar' | 'content';
}) {
  return (
    <PopUpButton
      context={context}
      label="Owner:"
      value={menu.choice}
      valueText={ownerButtonText(menu.choice, menu.options)}
      options={menu.options}
      onValueChange={menu.choose}
    />
  );
}
